import { useState, FormEvent } from "react";
import copy from "copy-to-clipboard";
import AzureLogo from "../assets/azure-logo.svg";
import MicrosoftLogo from "../assets/microsoft-logo.svg";
import HeroGrid from "../assets/hero-grid-figma.png";
import ShareGateLogo from "../assets/sharegate-wordmark.svg";

enum State { Waiting, Fetching, Found, NotFound }

const SearchIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeWidth="1.5" />
        <path d="m14 14 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

export const Hero = () => {
    const [state, setState] = useState<State>(State.Waiting);
    const [query, setQuery] = useState("");
    const [tenantId, setTenantId] = useState("");
    const [copied, setCopied] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!query.trim()) return;
        setState(State.Fetching);
        try {
            const response = await fetch(`https://login.microsoftonline.com/${query}/.well-known/openid-configuration`);
            const data = await response.json();
            const endPoint = data.token_endpoint;
            const id = endPoint.split("/")[3];
            setTenantId(id);
            setState(State.Found);
        } catch {
            setState(State.NotFound);
        }
    };

    const handleReset = () => {
        setQuery("");
        setTenantId("");
        setState(State.Waiting);
        setCopied(false);
    };

    const handleCopy = () => {
        copy(tenantId);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
    };

    return (
        <section className="hero">
            <img className="hero__grid" src={HeroGrid} alt="" aria-hidden="true" />

            <div className="hero__inner">
                <a
                    className="hero__brand"
                    href="https://sharegate.com/?utm_source=whatismytenantid&utm_campaign=whatismytenantid&utm_medium=bloc&utm_content=logo"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="ShareGate"
                >
                    <img src={ShareGateLogo} alt="ShareGate" />
                </a>

                {state !== State.Found ? (
                    <>
                        <h1 className="hero__title">
                            <span className="hero__title-line">
                                <span>What is your</span>
                                <span className="ms-logo"><img src={AzureLogo} alt="" /></span>
                                <span>Microsoft Azure</span>
                            </span>
                            <span className="hero__title-line">
                                <span>and</span>
                                <span className="ms-logo"><img src={MicrosoftLogo} alt="" /></span>
                                <span>Microsoft 365 tenant ID?</span>
                            </span>
                        </h1>

                        <form className="hero__form" onSubmit={handleSubmit} noValidate>
                            <label className="hero__label" htmlFor="domain-input">Enter your domain name</label>
                            <div className="hero__input-wrap">
                                <input
                                    id="domain-input"
                                    className="hero__input"
                                    type="text"
                                    autoFocus
                                    placeholder="contoso.com"
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        if (state === State.NotFound) setState(State.Waiting);
                                    }}
                                    disabled={state === State.Fetching}
                                />
                                <button
                                    className="hero__submit"
                                    type="submit"
                                    disabled={state === State.Fetching || !query.trim()}
                                >
                                    <SearchIcon /> Find my tenant
                                </button>
                            </div>
                            {state === State.NotFound && (
                                <div className="hero__error" role="alert">
                                    Couldn’t find your domain, make sure you entered it properly.
                                </div>
                            )}
                        </form>
                    </>
                ) : (
                    <>
                        <h1 className="hero__title hero__title--result">Here is the tenant ID for {query}</h1>
                        <div className="hero__form tenant-result">
                            <div className="hero__input-wrap hero__result-row">
                                <input
                                    className="hero__input tenant-result__id"
                                    readOnly
                                    value={tenantId}
                                    aria-label="Tenant ID"
                                />
                                <button className="hero__copy-btn" onClick={handleCopy} type="button">
                                    {copied ? "Copied!" : "Copy to clipboard"}
                                </button>
                            </div>
                            <button className="hero__reset" onClick={handleReset} type="button">
                                Look up another domain
                            </button>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};
